import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { permissions } from '../../utils/permissions-utils';
import API from '../../config/axiosConfig';
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import CommonLayout from '../../components/CommonLayout';
import Loader from '../../components/Loader';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import { Trash2 } from 'lucide-react';
import TagsInput from '../../components/TagsInput'; // Import the new component
import FileUpload from '../../features/trainings/components/FileUpload';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import InputWithIcon from '../../components/InputWithIcon';
import DatePicker from '../../components/DatePicker';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import UploadErrors from '../../components/UploadErrors';
import protocolIcon from '../../assets/protocols/protocol.svg';
import authorIcon from '../../assets/protocols/author.svg';
import dateSelectorIcon from '../../assets/protocols/date-selector.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import departmentIcon from '../../assets/protocols/department.svg';
import tagsIcon from '../../assets/protocols/tags.svg';
import uploadFileIcon from '../../assets/protocols/upload-file.svg';
import summaryIcon from '../../assets/protocols/summary.svg';
import './AddEditProtocol.scss';
import { primaryButtonStyle, secondaryButtonStyle, dialogActionsStyle } from '../../utils/sharedStyles';
import {
  DUPLICATE_ENGLISH_NAME_MESSAGE,
  getDepartmentNameErrors,
  getServerHebMessage,
} from '../../utils/department-utils';
import { cancelButton, confirmButton, deleteButtonInTitleMenu, pageActionsContainer } from '../../components/Buttons';
import PageTitle from '../../components/PageTitle';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Helper function to format date to DD/MM/YYYY for display
const formatDateForDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

// Helper function to parse DD/MM/YYYY format to ISO string
const parseDateFromDisplay = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('/');
  if (parts.length !== 3) return '';

  const day = parts[0].padStart(2, '0');
  const month = parts[1].padStart(2, '0');
  const year = parts[2];

  // Create ISO format YYYY-MM-DD
  return `${year}-${month}-${day}`;
};

// Helper function to format date to YYYY-MM-DD for input type="date" (kept for compatibility)
const formatDateToInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2)
    month = '0' + month;
  if (day.length < 2)
    day = '0' + day;

  return [year, month, day].join('-');
};

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#103C6E',
          '&:hover': {
            backgroundColor: '#0d3157',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          textAlign: 'right',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(-14px, -9px) scale(0.75)',
          '&[data-shrink="false"]': {
            transform: 'translate(14px, 16px) scale(1)',
          },
        },
      },
    },
  },
});

function AddEditProtocol() {
  const { user, hospitals, createDepartment, fetchOutdatedProtocols } = useContext(UserContext);
  const [protocol, setProtocol] = useState({
    name: '',
    hospital: '',
    department: '',
    tags: [],
    files: [], // new files
    existingFiles: [], //exsiting files
    filesToDelete: [], // files for delete
    currentRevision: null,
    authors: [],
    lastValidatedAt: '',
    summary: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [newDepartment, setNewDepartment] = useState({ nameHebrew: '', nameEnglish: '' });
  const [newDepartmentErrors, setNewDepartmentErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Save failures used to be console-only: a protocol whose upload the server
  // rejected simply re-enabled the button and looked like nothing happened.
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const uniqueHospitals = user ? [...new Set(user.roles.map(role => role.hospital))] : [];
  const isSingleHospitalUser = uniqueHospitals.length === 1 && !user.isSuperAdmin;

  const uniqueDepartments = user ? [...new Set(user.roles.map(role => role.department))] : [];
  const isSingleDepartmentUser = uniqueDepartments.length === 1 && !user.isSuperAdmin;

  useEffect(() => {
    if (user) {
      if (!id && !permissions.canCreate(user)) {
        navigate('/protocols');
      }
    }
  }, [user, id, navigate]);

  useEffect(() => {
    if (user) {
      if (isSingleHospitalUser) {
        const userHospital = uniqueHospitals[0];
        setProtocol(prev => ({ ...prev, hospital: userHospital }));
      }
      if (isSingleDepartmentUser) {
        const userDepartment = uniqueDepartments[0];
        setProtocol(prev => ({ ...prev, department: userDepartment }));
      }

      if (!id) {
        // Handle the query parameter for new protocols
        const searchParams = new URLSearchParams(location.search);
        const defaultDeptParam = searchParams.get('dep');
        if (defaultDeptParam) {
          let foundHospital = null;
          let foundDept = null;

          for (const h of hospitals) {
            const dept = h.departments.find(d => 
              d._id === defaultDeptParam || 
              d.nameHebrew === defaultDeptParam || 
              d.nameEnglish === defaultDeptParam
            );
            if (dept) {
              foundHospital = h;
              foundDept = dept;
              break;
            }
          }

          if (foundDept) {
            setProtocol(prev => ({
              ...prev,
              hospital: foundHospital._id,
              department: foundDept._id
            }));
          }
        }
      }
    }
    if (id) {
      fetchProtocol();
    }
  }, [user, id, isSingleHospitalUser, isSingleDepartmentUser, hospitals, location.search]);

  useEffect(() => {
    const fetchAutocompleteData = async () => {
      try {
        const response = await API.get('/api/system/autocomplete');
        setAllTags(response.data.tags || []);
      } catch (error) {
        console.error('Error fetching autocomplete data:', error);
      }
    };
    fetchAutocompleteData();
  }, []);

  const fetchProtocol = async () => {
    setIsLoading(true);
    try {
      const response = await API.get(`/api/protocols/view/${id}`);
      const fetchedProtocol = {
        name: response.data.name || '',
        hospital: response.data.hospital?._id || '',
        department: response.data.departments?.[0] || '',
        tags: response.data.tags || [],
        files: [],
        existingFiles: response.data.currentRevision?.files.map(f => ({ ...f, displayName: f.displayName || f.name.split('.').slice(0, -1).join('.') })) || [], // קבצים קיימים
        currentRevision: response.data.currentRevision,
        authors: response.data.authors || [],
        lastValidatedAt: response.data.lastValidatedAt ? formatDateForDisplay(new Date(response.data.lastValidatedAt)) : '',
        summary: response.data.summary || ''
      };
      setProtocol(fetchedProtocol);
      if (!permissions.canEdit(user, { department: fetchedProtocol.department })) {
        navigate('/protocols');
      }
    } catch (error) {
      console.error('Fetching protocol failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!protocol.name.trim()) newErrors.name = 'שדה חובה';
    if (!protocol.hospital) newErrors.hospital = 'שדה חובה';
    if (!protocol.department) newErrors.department = 'שדה חובה';
    if (!id && (!protocol.files || protocol.files.length === 0)) {
      newErrors.files = 'נדרש לפחות קובץ אחד';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', protocol.name);
      formData.append('hospital', protocol.hospital);
      formData.append('departments[]', protocol.department);
      if (protocol.authors && protocol.authors.length > 0) {
        protocol.authors.forEach(author => formData.append('authors[]', author));
      } else {
        formData.append('authors[]', '');
      }
      formData.append('summary', protocol.summary || '');
      if (protocol.lastValidatedAt) {
        const isoDate = parseDateFromDisplay(protocol.lastValidatedAt);
        if (isoDate) {
          formData.append('lastValidatedAt', new Date(isoDate).toISOString());
        }
      }
      if (protocol.tags && protocol.tags.length > 0) {
        protocol.tags.forEach(tag => formData.append('tags[]', tag));
      } else {
        formData.append('tags[]', '');
      }

      if (protocol.filesToDelete && protocol.filesToDelete.length > 0) {
        protocol.filesToDelete.forEach(relativeUrl => {
          formData.append('filesToDelete[]', relativeUrl);
        });
      }

      protocol.files.forEach((file, index) => {
        formData.append('files', file);
        formData.append('fileDisplayNames[]', file.displayName);
      });

      if (id) {
        formData.append('existingFiles', JSON.stringify(protocol.existingFiles));
      }

      const endpoint = id ? `/api/protocols/${id}` : '/api/protocols';
      const method = id ? 'put' : 'post';
      // handlingErrorLocally: the failure belongs next to the save button, not
      // in a floating toast that vanishes after five seconds while the form
      // still looks unsaved.
      await API[method](endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        handlingErrorLocally: true
      });
      await fetchOutdatedProtocols();
      navigate('/protocols');
    } catch (error) {
      console.error('Submitting protocol failed:', error);
      setSubmitError(
        error.serverError?.hebMessage
        || (id ? 'שמירת הפרוטוקול נכשלה. אנא נסה שנית.' : 'יצירת הפרוטוקול נכשלה. אנא נסה שנית.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHospitalChange = (e) => {
    const newHospitalId = e.target.value;
    setProtocol(prev => ({ ...prev, hospital: newHospitalId, department: '' }));
    setErrors(prev => ({ ...prev, hospital: '', department: '' }));
  };

  const handleCreateDepartment = async () => {
    // Department names must be unique within a hospital - warn before hitting the server.
    const duplicateErrors = getDepartmentNameErrors(
      hospitals.find(h => h._id === protocol.hospital)?.departments,
      newDepartment
    );
    if (!newDepartment.nameHebrew.trim()) {
      duplicateErrors.nameHebrew = 'שדה חובה';
    }
    if (Object.keys(duplicateErrors).length > 0) {
      setNewDepartmentErrors(duplicateErrors);
      return;
    }

    try {
      const createdDepartment = await createDepartment(protocol.hospital, newDepartment);
      setProtocol(prev => ({
        ...prev,
        department: createdDepartment._id
      }));
      setShowModal(false);
      setNewDepartment({ nameHebrew: '', nameEnglish: '' });
      setNewDepartmentErrors({});
    } catch (error) {
      console.error('Creating department failed:', error);
      const hebMessage = getServerHebMessage(error);
      setNewDepartmentErrors(
        hebMessage === DUPLICATE_ENGLISH_NAME_MESSAGE
          ? { nameEnglish: hebMessage }
          : { nameHebrew: hebMessage || 'יצירת המחלקה נכשלה. אנא נסה שנית.' }
      );
    }
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSuccess = () => {
    navigate('/protocols');
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/api/protocols/${id}`);
      setDeleteConfirmOpen(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error('Deletion failed:', error);
      const message = error.response?.data?.message || 'שגיאה במחיקת הפרוטוקול. אנא נסה שנית.';
      setErrorMessage(message);
      setShowError(true);
    }
  };

  // Removed handleAddTag, handleAddTagOnBlur, handleRemoveTag

  // New handler for TagsInput component
  const handleTagsChange = (newTags) => {
    setProtocol(prev => ({ ...prev, tags: newTags }));
  };

  const handleAuthorsChange = (newAuthors) => {
    setProtocol(prev => ({ ...prev, authors: newAuthors }));
  };

  // New handler for FileUpload component
  const handleFilesChange = (newFiles) => {
    // Separate new files from existing ones
    const newUploadedFiles = newFiles.filter(file => !file.isInitial);
    const existingFiles = newFiles.filter(file => file.isInitial && file.originalFile);

    // Find deleted existing files
    const currentExistingUrls = existingFiles.map(f => f.originalFile.relativeUrl);
    const originalExistingUrls = protocol.existingFiles.map(f => f.relativeUrl);
    const deletedFiles = originalExistingUrls.filter(url => !currentExistingUrls.includes(url));

    setProtocol(prev => ({
      ...prev,
      files: newUploadedFiles.map(file => Object.assign(file, {
        displayName: file.displayName || file.name.split('.').slice(0, -1).join('.')
      })),
      existingFiles: existingFiles.map(file => ({
        ...file.originalFile,
        displayName: file.displayName || file.originalFile.displayName
      })),
      filesToDelete: [...(prev.filesToDelete || []), ...deletedFiles]
    }));
    setErrors(prev => ({ ...prev, files: '' }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  };
  return (
    <CommonLayout>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          {/* Removed LocalizationProvider */}
          <div className="add-edit-protocol-container" style={{ position: 'relative' }}>
            <PageTitle title={id ? 'עריכת פרוטוקול' : 'פרוטוקול חדש'}>
              {id && (
                <button onClick={openDeleteConfirm} style={deleteButtonInTitleMenu} title="מחק פרוטוקול">
                  <Trash2 size={16} />
                </button>
              )}
            </PageTitle>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <InputWithIcon
                  icon={protocolIcon}
                  placeholder="שם הפרוטוקול"
                  value={protocol.name}
                  onChange={(e) => setProtocol({ ...protocol, name: e.target.value })}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </div>
              <div className="form-group">
                <TagsInput
                  tags={protocol.authors}
                  onChange={handleAuthorsChange}
                  label="שמות מחברי הפרוטוקול"
                  icon={authorIcon}
                />
              </div>
              <div className="form-group">
                <DatePicker
                  placeholder="תאריך תיקוף אחרון - DD/MM/YYYY"
                  value={protocol.lastValidatedAt}
                  onChange={(newValue) => {
                    setProtocol({ ...protocol, lastValidatedAt: newValue });
                    setErrors(prev => ({ ...prev, lastValidatedAt: '' }));
                  }}
                  error={!!errors.lastValidatedAt}
                  helperText={errors.lastValidatedAt}
                />
              </div>
              {!isSingleHospitalUser && (
                <div className="form-group">
                  <DropdownWithIcon
                    icon={hospitalIcon}
                    placeholder="בית חולים"
                    value={protocol.hospital}
                    onChange={handleHospitalChange}
                    required
                    error={!!errors.hospital}
                    helperText={errors.hospital}
                  >
                    {hospitals.map(hospital => (
                      <option key={hospital._id} value={hospital._id}>
                        {hospital.nameHebrew}
                      </option>
                    ))}
                  </DropdownWithIcon>
                </div>
              )}
              {!isSingleDepartmentUser && (
                <div className="form-group">
                  <DropdownWithIcon
                    icon={departmentIcon}
                    placeholder="מחלקה"
                    value={protocol.department}
                    onChange={(e) => {
                      if (e.target.value === 'create-new') {
                        setShowModal(true);
                        return;
                      }
                      setProtocol({ ...protocol, department: e.target.value });
                      setErrors(prev => ({ ...prev, department: '' }));
                    }}
                    required
                    disabled={!protocol.hospital}
                    error={!!errors.department}
                    helperText={errors.department}
                  >
                    {protocol.hospital && hospitals.find(h => h._id === protocol.hospital)?.departments.map(department => (
                      <option key={department._id} value={department._id}>
                        {department.nameHebrew}
                      </option>
                    ))}
                  </DropdownWithIcon>
                </div>
              )}
              <div className="form-group">
                <TagsInput
                  tags={protocol.tags}
                  onChange={handleTagsChange}
                  autocompleteOptions={allTags}
                  label="תגיות"
                  icon={tagsIcon}
                />
              </div>
              <div className="form-group">
                <InputWithIcon
                  icon={summaryIcon}
                  placeholder="תקציר"
                  multiline
                  rows={4}
                  value={protocol.summary}
                  onChange={(e) => setProtocol({ ...protocol, summary: e.target.value })}
                  error={!!errors.summary}
                  helperText={errors.summary}
                />
              </div>
              <div className="form-group">
                <FileUpload
                  onFilesChange={handleFilesChange}
                  initialFiles={[...protocol.existingFiles, ...protocol.files]}
                  maxFiles={20}
                  maxSize={20 * 1024 * 1024}
                  // Kept in step with the server whitelist in config/multer.js.
                  // The picker is only a UX hint, but offering less than the
                  // server accepts hides usable formats, and offering more
                  // fails the whole save on one rejected file.
                  accept={{
                    'application/pdf': ['.pdf'],
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/png': ['.png'],
                    'image/webp': ['.webp'],
                    'image/gif': ['.gif'],
                    'image/bmp': ['.bmp'],
                    'image/tiff': ['.tif', '.tiff'],
                    'video/mp4': ['.mp4'],
                    'video/quicktime': ['.mov'],
                    'video/x-msvideo': ['.avi'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'application/vnd.ms-excel': ['.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    'application/vnd.ms-powerpoint': ['.ppt'],
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
                    'application/rtf': ['.rtf'],
                    'text/plain': ['.txt'],
                    'text/csv': ['.csv'],
                    'message/rfc822': ['.eml'],
                    'application/vnd.ms-outlook': ['.msg'],
                  }}
                  dropzoneText="בחר קבצים או גרור לכאן"
                  dropzoneSubtext="מסמכים (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, RTF), תמונות (PNG, JPG, WEBP, GIF, BMP, TIF), וידאו (MP4, MOV, AVI), דואר (EML, MSG) - עד 20MB לקובץ"
                  enableFileNameEdit={true}
                />
                {errors.files && <FormHelperText error>{errors.files}</FormHelperText>}
              </div>
              {/* autoDismissMs=0: a failed save must stay on screen until the
                  user acts on it, unlike a rejected-file notice. */}
              <UploadErrors
                messages={submitError ? [submitError] : []}
                onDismiss={() => setSubmitError('')}
                autoDismissMs={0}
              />
              <div style={pageActionsContainer}>
                <LoadingSubmitButton
                  type="submit"
                  style={confirmButton}
                  isLoading={isSubmitting}
                  text={id ? 'עדכן' : 'צור'}
                  loadingText={id ? 'מעדכן...' : 'יוצר...'}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  style={cancelButton}
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                >
                  ביטול
                </button>
              </div>
            </form>
            <Dialog open={showModal} onClose={() => setShowModal(false)}>
              <DialogTitle>צור מחלקה חדשה</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="שם המחלקה בעברית"
                  fullWidth
                  value={newDepartment.nameHebrew}
                  onChange={(e) => {
                    setNewDepartment({ ...newDepartment, nameHebrew: e.target.value });
                    setNewDepartmentErrors(prev => ({ ...prev, nameHebrew: '' }));
                  }}
                  error={!!newDepartmentErrors.nameHebrew}
                  helperText={newDepartmentErrors.nameHebrew}
                  required
                />
                <TextField
                  margin="dense"
                  label="שם המחלקה באנגלית"
                  fullWidth
                  value={newDepartment.nameEnglish}
                  onChange={(e) => {
                    setNewDepartment({ ...newDepartment, nameEnglish: e.target.value });
                    setNewDepartmentErrors(prev => ({ ...prev, nameEnglish: '' }));
                  }}
                  error={!!newDepartmentErrors.nameEnglish}
                  helperText={newDepartmentErrors.nameEnglish}
                  required
                />
              </DialogContent>
              <DialogActions style={dialogActionsStyle}>
                <Button
                  style={secondaryButtonStyle}
                  onClick={() => {
                    setShowModal(false);
                    setNewDepartmentErrors({});
                  }}
                >
                  ביטול
                </Button>
                <Button style={primaryButtonStyle} onClick={handleCreateDepartment} color="primary">צור מחלקה</Button>
              </DialogActions>
            </Dialog>

            <DeleteConfirmationDialog
              open={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
              onConfirm={handleDelete}
              title={`האם אתה בטוח שברצונך למחוק את הפרוטוקול ${protocol.name}?`}
              showError={showError}
              errorMessage={errorMessage}
              onCloseError={() => setShowError(false)}
            />

            <Loader isLoading={isLoading} />
          </div>
        </ThemeProvider>
      </CacheProvider>
    </CommonLayout>
  );
}

export default AddEditProtocol;
